/**
 * Shared badges + small formatters for the Observed-skills surface (O-769).
 * Used by ObservedSkillsTable and EndpointSkillUsagePanel.
 */
import { SKILL_ORIGIN, skillOriginLabel, skillVendorLabel } from "../../lib/skillsClient.js";

const BADGE_BASE =
    "inline-flex items-center rounded-[5px] border px-[5px] py-[1px] text-[11px]";

// 5-way provenance palette. Tailwind palette colors for the emergent origins
// (User / Project) so they pop against the muted catalog rows; CSS-var muted
// tones for Plugin / Built-in / Unknown.
const ORIGIN_PALETTE = {
    [SKILL_ORIGIN.USER_LOCAL]:     "border-purple-400/40 text-purple-300",
    [SKILL_ORIGIN.PROJECT_LOCAL]:  "border-sky-400/40 text-sky-300",
    [SKILL_ORIGIN.ORIGIN_MANAGED]: "border-[color:var(--green)] text-[color:var(--green)]",
    [SKILL_ORIGIN.PLUGIN]:         "border-[color:var(--line)] text-[color:var(--muted)]",
    [SKILL_ORIGIN.BUILT_IN]:       "border-[color:var(--line)] text-[color:var(--muted-soft)]",
};

export function OriginBadge({ origin, pluginId }) {
    const palette = ORIGIN_PALETTE[origin] ?? "border-[color:var(--line)] text-[color:var(--muted-soft)]";
    const isPlugin = origin === SKILL_ORIGIN.PLUGIN && pluginId;
    // A long plugin id (e.g. "anthropic/figma-mcp") would otherwise widen the
    // Provenance column and clip its neighbors — cap + truncate, full id on hover.
    const label = isPlugin ? `Plugin: ${pluginId}` : skillOriginLabel(origin);
    return (
        <span
            className={`${BADGE_BASE} max-w-[10rem] truncate ${palette}`}
            title={isPlugin ? pluginId : undefined}
        >
            {label}
        </span>
    );
}

export function VendorBadge({ vendor }) {
    return (
        <span className={`${BADGE_BASE} border-[color:var(--line)] text-[color:var(--muted)]`}>
            {skillVendorLabel(vendor)}
        </span>
    );
}

// "auto" marker rendered next to an auto-derived display_disambiguator.
export function AutoBadge() {
    return (
        <span
            className={`${BADGE_BASE} border-[color:var(--line)] text-[color:var(--muted-soft)]`}
            title="Auto-derived label (author → description → content hash). Operators can override it on the Managed tab."
        >
            auto
        </span>
    );
}

// First `n` chars of a description with an ellipsis. Used in dense table cells;
// the full text rides in a `title` on the cell.
export function descriptionPrefix(text, n = 30) {
    if (!text) return "";
    return text.length > n ? `${text.slice(0, n)}…` : text;
}

// First 8 chars of a content sha256 — the variant fallback label when a row
// has no display_disambiguator.
export function shortSha(sha) {
    return sha ? sha.slice(0, 8) : "";
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// Compact relative timestamp ("3d ago"). Accepts epoch ms, an ISO
// string, or a numeric string (a numeric string fed to new Date()
// yields Invalid Date, which used to silently render "—").
// Returns "—" for absent/unparseable input.
export function formatRelativeTime(when) {
    if (!when) return "—";
    // Digit-only test, not Number(): Number accepts whitespace-only ("" → 0),
    // hex, and exponent strings, which would render as 1969 dates instead
    // of the "—" they produced before.
    const coerced = typeof when === "string" && /^-?\d+(\.\d+)?$/.test(when.trim())
        ? Number(when)
        : when;
    const then = new Date(coerced).getTime();
    if (Number.isNaN(then)) return "—";
    const delta = Date.now() - then;
    if (delta < MINUTE) return "just now";
    if (delta < HOUR) return `${Math.floor(delta / MINUTE)}m ago`;
    if (delta < DAY) return `${Math.floor(delta / HOUR)}h ago`;
    if (delta < 30 * DAY) return `${Math.floor(delta / DAY)}d ago`;
    return new Date(coerced).toLocaleDateString();
}

// Ellipsize a long filesystem path in the middle: head + … + tail.
export function ellipsizePath(path, head = 12, tail = 24) {
    if (!path || path.length <= head + tail + 1) return path || "";
    return `${path.slice(0, head)}…${path.slice(-tail)}`;
}
